import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import styled from 'styled-components';

import { GIC_MESSAGES } from '@ratehub/inv-common';
import {
    ANALYTICS_KEY,
    PrimaryButton,
    ProviderDetails,
    getDesignationMessage,
    DefaultIntlFormatValues,
    FormattedNumberRaisedSymbol,
} from '@ratehub/base-ui';

import GICsAccountPropType from '../definitions/GICsAccountPropType';
import PFAWinnerPill from './PFAWinnerPill';


function ProductTableSponsoredGIC({ gicAccount, onRateClick, className }) {
    const {
        term,
        type,
        provider,
        interest,
        isPFAWinner,
        isSponsored,
        minimumInvestment,
    } = gicAccount;


    return (
        <div
            className={classNames(className, 'rh-p-0_75 rh-bg-coconut rh-b-1px-solid-stone')}
            ref={self => {
                if (self) {
                    self[ANALYTICS_KEY] = gicAccount;
                }
            }}
        >
            {/* when used on top of the Best GIC Rates table, we expect it will always be sponsored,
                but do the test so this component could possibly be used elsewhere for non-sponsored GICs */ }
            <If condition={isSponsored}>
                <p className="rh-text-2xs rh-mt-0 rh-mb-0_5 rh-text-lowercase">
                    {getDesignationMessage({ isSponsored })}
                </p>
            </If>

            <div className="rh-display-flex rh-justify-content-space-between">
                <div className="rate-summary rh-mr-2">
                    <span className="interest-rate rh-title-m weight-bold">
                        <FormattedNumberRaisedSymbol
                            value={interest / 100}
                            format="percent2"
                        />
                    </span>
                    <p className="info rh-text-2xs rh-mb-0 weight-medium">
                        <FormattedMessage
                            {...GIC_MESSAGES.gicTermNameShort}
                            values={{
                                term,   // in months
                                years: term / 12,
                            }}
                        />
                        <FormattedMessage
                            id="web-components.single-gic.info"
                            defaultMessage={` {type, select,
    registered {registered}
    other {non-registered}
} GIC rate with a minimum investment of {minimumInvestment, number, CAD0}`}
                            values={{
                                ...DefaultIntlFormatValues,
                                type,
                                minimumInvestment,
                            }}
                        />
                    </p>
                </div>

                <div className="name-cta rh-display-flex rh-flex-wrap rh-align-items-center">
                    <div className="provider rh-display-flex rh-align-items-center rh-flex-wrap rh-gap-0_5">
                        <ProviderDetails
                            providerIcon={provider?.logo}
                            providerName={provider?.name}
                            hasLogoBorder={true}
                        />
                        <If condition={isPFAWinner}>
                            <PFAWinnerPill />
                        </If>
                    </div>

                    <div className="cta-container">
                        <PrimaryButton
                            className="rh-mt-0_5"
                            size="small"
                            message={(
                                <FormattedMessage
                                    id="web-components.single-gic.cta"
                                    defaultMessage="Get rate"
                                />
                            )}
                            onClick={() => onRateClick(gicAccount)}
                            data-name="cta-click-gic"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

ProductTableSponsoredGIC.propTypes = {
    gicAccount: GICsAccountPropType.isRequired,
    onRateClick: PropTypes.func.isRequired,
    className: PropTypes.string,
};

export default styled(ProductTableSponsoredGIC)`
    .rate-summary {
        flex: 0 1 14rem;
        min-width: 170px;

        .interest-rate {
            letter-spacing: -0.3333px;
        }
    }

    .name-cta {
        flex: 1 1 680px;

        .provider {
            flex: 25 1 250px;
        }

        .cta-container {
            flex: 1 0 8rem;   // increase grow to equalize button width in very wide layout
            max-width: 14rem;
        }
    }
`;
